/**
 * Session context.
 *
 * Holds the signed-in user, their resolved permissions, and the schools they can
 * see. Refetched from the server rather than trusted from a token, so a role change
 * or a suspension takes effect on the next page load.
 *
 * `can()` decides what to render. It is never the security boundary — the API
 * re-checks every action and row-level security re-checks every row. This exists so
 * the UI does not offer buttons that would be refused.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, apiFetch, setUnauthorisedHandler, tokenStore } from './api.ts';

export interface Assignment {
  id: string;
  role_key: string;
  role_name: string;
  scope: 'global' | 'school' | 'department';
  school_id: string | null;
  school_name: string | null;
  department_id: string | null;
  department_name: string | null;
  approval_limit_gbp: string | null;
  valid_until: string | null;
}

export interface SchoolRef {
  id: string;
  code: string;
  name: string;
}

export interface Session {
  user: { id: string; email: string; fullName: string; mfaSatisfied: boolean };
  permissions: string[];
  assignments: Assignment[];
  accessibleSchools: SchoolRef[];
  isGroupWide: boolean;
  actingOnBehalfOf: string | null;
  unreadNotifications: number;
}

interface SessionContextValue {
  session: Session | null;
  loading: boolean;
  can: (...permissions: string[]) => boolean;
  /** The school currently in focus. Group-wide users can change it. */
  activeSchoolId: string | null;
  setActiveSchoolId: (id: string | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

const ACTIVE_SCHOOL_KEY = 'asp.activeSchool';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSchoolId, setActiveSchoolIdState] = useState<string | null>(
    () => localStorage.getItem(ACTIVE_SCHOOL_KEY),
  );

  const load = useCallback(async () => {
    if (!tokenStore.get()) {
      setSession(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api.get<Session>('/api/auth/me');
      setSession(data);

      // Default the school selector to the only school someone can see, which is
      // the common case — most staff belong to exactly one.
      setActiveSchoolIdState((current) => {
        if (current && data.accessibleSchools.some((s) => s.id === current)) return current;
        return data.accessibleSchools.length === 1 ? data.accessibleSchools[0]!.id : null;
      });
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthorisedHandler(() => setSession(null));
    void load();
  }, [load]);

  const setActiveSchoolId = useCallback((id: string | null) => {
    setActiveSchoolIdState(id);
    if (id) localStorage.setItem(ACTIVE_SCHOOL_KEY, id);
    else localStorage.removeItem(ACTIVE_SCHOOL_KEY);
  }, []);

  const can = useCallback(
    (...permissions: string[]) =>
      session ? permissions.some((p) => session.permissions.includes(p)) : false,
    [session],
  );

  const signOut = useCallback(async () => {
    try {
      await apiFetch('/api/auth/sign-out', { method: 'POST' });
    } catch {
      /* signing out locally matters more than the server acknowledging it */
    }
    tokenStore.clear();
    setSession(null);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ session, loading, can, activeSchoolId, setActiveSchoolId, refresh: load, signOut }),
    [session, loading, can, activeSchoolId, setActiveSchoolId, load, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/** Throws if called before sign-in, so authenticated screens can rely on it. */
export function useUser(): Session {
  const { session } = useSession();
  if (!session) throw new Error('No session');
  return session;
}
